import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { configureApiClient, API } from '../api/client';
import type { LoginResponse, Usuario } from '../api/types';

interface Session {
  access: string;
  refresh: string;
  usuario: Usuario;
}

interface AuthCtx {
  status: 'loading' | 'anon' | 'authed';
  session: Session | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  tiene: (permiso: string) => boolean;
}

const AuthContext = createContext<AuthCtx | null>(null);

const STORAGE_KEY = 'arepas:session';

type StoredSession = { access: string; refresh: string; usuario: Usuario };

function readPersisted(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writePersisted(s: StoredSession | null): void {
  try {
    if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<'loading' | 'anon' | 'authed'>('loading');
  const [session, setSession] = useState<Session | null>(null);
  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;

  useEffect(() => {
    configureApiClient({
      getTokens: () => ({
        access: sessionRef.current?.access ?? null,
        refresh: sessionRef.current?.refresh ?? null,
      }),
      setTokens: (t) => {
        if (!sessionRef.current || !t.access || !t.refresh) return;
        const next: Session = { ...sessionRef.current, access: t.access, refresh: t.refresh };
        sessionRef.current = next;
        setSession(next);
        writePersisted(next);
      },
      onUnauthorized: () => {
        sessionRef.current = null;
        setSession(null);
        writePersisted(null);
        setStatus('anon');
      },
    });

    const stored = readPersisted();
    if (stored) {
      setSession({ ...stored });
      setStatus('authed');
    } else {
      setStatus('anon');
    }
  }, []);

  const ctx = useMemo<AuthCtx>(
    () => ({
      status,
      session,
      login: async (email, password) => {
        const data = await API.post<LoginResponse>('/auth/login', { email, password });
        const s: Session = {
          access: data.accessToken,
          refresh: data.refreshToken,
          usuario: data.usuario,
        };
        sessionRef.current = s;
        setSession(s);
        writePersisted(s);
        setStatus('authed');
      },
      logout: async () => {
        const refresh = sessionRef.current?.refresh;
        if (refresh) {
          try {
            await API.post('/auth/logout', { refreshToken: refresh });
          } catch {
            /* ignore */
          }
        }
        sessionRef.current = null;
        setSession(null);
        writePersisted(null);
        setStatus('anon');
      },
      tiene: (p) => session?.usuario.permisos.includes(p) ?? false,
    }),
    [status, session],
  );

  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const c = useContext(AuthContext);
  if (!c) throw new Error('useAuth fuera de AuthProvider');
  return c;
}
